import React, { useMemo } from 'react';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { IconButton, Grid, Typography } from '@material-ui/core';
import { Avatar as TooltipAvatar } from 'app/components/withTooltip';
import { Table } from 'altus-ui-components';
import { EMPTY_LIST, Format } from 'app/app.constants';
import moment from 'moment';
import {
  ACTIVITY_TYPES,
  ActivityTypes,
} from 'features/wells/overview/activityLog/helpers/activityLog.constants';
import StatusChip from 'features/wells/overview/activityLog/components/StatusChip';
import { getRichEditorText, stringToEditorState } from 'utils/app.util';
import EditorStateViewer from 'app/components/EditorStateViewer';

const ActivityLogsTable = ({
  activityLogs = EMPTY_LIST,
  setIsEdit,
  deleteActivityLog,
}) => {
  const columns = useMemo(
    () => [
      {
        xs: 3,
        Header: 'Activity',
        accessor: (log) => log.activityName,
      },
      {
        xs: 2,
        Header: 'Start Date',
        accessor: (log) => moment(log.startDate).format(Format.date_dayfirst),
      },
      {
        xs: 2,
        Header: 'End Date',
        accessor: (log) => moment(log.endDate).format(Format.date_dayfirst),
      },
      {
        xs: 2,
        Header: 'Type',
        accessor: (log) =>
          ACTIVITY_TYPES.find((type) => type.id === log.activityType).name,
      },
      {
        xs: 2,
        Header: 'Status',
        accessor: (log) => <StatusChip status={log.status} />,
      },
      {
        xs: 2,
        Header: 'Responsible',
        accessor: (log) => log.responsibleUser?.fullName,
        Cell: ({ row }) => {
          const user = row.original.responsibleUser;

          return user ? (
            <Grid container alignItems={'center'}>
              <Grid item xs={2}>
                <TooltipAvatar title={user.fullName} src={user.avatar}>
                  {!user.avatar && user.initials}
                </TooltipAvatar>
              </Grid>
              <Grid item xs={2} />
              <Grid item xs={6}>
                <Typography variant="body2">{user.fullName}</Typography>
              </Grid>
            </Grid>
          ) : (
            '-'
          );
        },
      },
      {
        xs: 2,
        Header: 'Comment',
        accessor: (log) => getRichEditorText(log.comment),
        Cell: ({ row }) => {
          const commentEditorState = row.original.comment
            ? stringToEditorState(row.original.comment)
            : null;

          return commentEditorState ? (
            <Typography>
              <EditorStateViewer editorState={commentEditorState} />
            </Typography>
          ) : (
            '-'
          );
        },
      },
      {
        xs: 2,
        Header: 'Solutions for Mitigating Challenges',
        accessor: (log) => getRichEditorText(log.mitigationSolutions),
        Cell: ({ row }) => {
          const mitigationEditorState = row.original.mitigationSolutions
            ? stringToEditorState(row.original.mitigationSolutions)
            : null;

          return mitigationEditorState ? (
            <Typography>
              <EditorStateViewer editorState={mitigationEditorState} />
            </Typography>
          ) : (
            '-'
          );
        },
      },
      {
        xs: 2,
        id: 'delete',
        Cell: ({ row }) => {
          const log = row.original;
          return (
            log.activityType === ActivityTypes.ACTIVITY && (
              <>
                <IconButton
                  color="inherit"
                  onClick={() => setIsEdit(log)}
                  aria-label="Edit"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  title="Delete"
                  color="inherit"
                  onClick={() => deleteActivityLog(log.activityLogId)}
                  aria-label="delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )
          );
        },
      },
    ],
    [setIsEdit, deleteActivityLog],
  );

  return (
    <>
      <Table
        subrowOffset={3}
        items={activityLogs.size > 0 ? activityLogs : EMPTY_LIST}
        columns={columns}
        useGlobalFilter={false}
        noItemsMessage="No Activity has been added yet."
      />
    </>
  );
};

export default ActivityLogsTable;
